import { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Stack, Card, Title, Text, Button, Group } from '@repletoryreach/sdk-ui';
import { useT, type Translate } from '../../i18n/I18nProvider';

interface BoundaryProps {
  children: ReactNode;
  t: Translate;
}

class Boundary extends Component<BoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    const { t } = this.props;
    if (this.state.error) {
      return (
        <Card withBorder>
          <Stack gap="sm" align="flex-start">
            <Title order={4}>{t('shell.error.title')}</Title>
            <Text fz="sm" c="var(--rr-color-text-muted, #94a3b8)">
              {this.state.error.message || t('shell.error.body')}
            </Text>
            <Group gap="sm">
              <Button size="xs" variant="outline" onClick={() => this.setState({ error: null })}>
                {t('shell.error.retry')}
              </Button>
              {/*
                "Try again" only resets local state, which does nothing when the
                error came from cached data or a bad module. A real reload is
                the escape hatch (_UI_improvements/tenant/_app-shell.md §6).
              */}
              <Button size="xs" variant="subtle" onClick={() => window.location.reload()}>
                {t('shell.error.reload')}
              </Button>
            </Group>
          </Stack>
        </Card>
      );
    }
    return this.props.children;
  }
}

/** Catches render errors from the active route; the shell + nav stay alive. Resets on navigation. */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const t = useT();
  return <Boundary key={location.pathname} t={t}>{children}</Boundary>;
}
